import { ConfigObj, PubSub, DomHelpers, Ajax } from '../../utils'

// Postcode anywhere docs: https://www.loqate.com/resources/support/apis/capture/

const _defaultOpts = {
  limit: 20,
  countries: 'GB',
  line1Id: '',
  line2Id: '',
  cityId: '',
  countyId: '',
  postcodeId: '',
  addressId: undefined,
  manualLinkAction: undefined
}

const MODULE_NAME = 'ps-address-lookup'

const API_KEY = 'TF39-JU92-YE41-EX61' // NOTE: This is a live API key, for soft testing please use the following test postcode: WR5 3DA
const MANUAL_LINK_CONTENT = 'Can\'t find your address? Enter it manually'

const CONTENT = {
  FILTER_PROMPT: 'Filter search',
  NO_RESULTS: 'No results found',
  CLEAR_BUTTON_VALUE: 'Clear'
}

// Scoped CSS selectors used for document.manipulation and styling
const SELECTORS = {
  RESULTS_CONTAINER: `${MODULE_NAME}__result-container`,
  RESULTS: `${MODULE_NAME}__result-list`,
  MANUAL_LINK: `${MODULE_NAME}__manual-link`,
  FILTER_INPUT: `${MODULE_NAME}__filter-input`,
  FILTER_PLACEHOLDER: `${MODULE_NAME}__filter-placeholder`,
  FILTER_INPUT_FIELD: `${MODULE_NAME}__filter-input-field`,
  DUMMY_ADDRESS_FIELD: `${MODULE_NAME}__dummy-address-field`,
  BUTTON_SEARCH: `${MODULE_NAME}__button-search`,
  BUTTON_CLEAR: `${MODULE_NAME}__button-clear`,
}

const LOOKUP_TYPES = {
  RETRIEVE: 'RETRIEVE',
  FIND: 'FIND'
}

const DEFAULT_COUNTY_ID = 1000

/**
 * AddressLookup class - 
 * @param {HTMLElement} $el - the element to which the class and its listeners are applied
 * @param {Object} opts - The user options to overwrite the default opts
 */
export default class AddressLookup {
  constructor($el, opts) {
    this.$el = $el
    this.opts = ConfigObj.merge(_defaultOpts, opts)
  }

  /**
  * Class initialisation method
  */
  init() {
    this.$searchButton = document.createElement('button')
    this.$searchButton.classList.add(SELECTORS.BUTTON_SEARCH)
    this.$searchButton.innerText = 'Search'

    const $container = document.createElement('div')
    $container.classList.add(`${MODULE_NAME}`)

    this.$el.parentElement.insertBefore($container, this.$el)

    $container.appendChild(this.$searchButton)
    $container.appendChild(this.$el)

    // Re-assigning this.$el and this.$postcodeInput to the new document.structure to allow for event delegation
    this.$postcodeInput = this.$el
    this.$el = $container
    this.$el.id = `addressLookup_${Date.now()}`

    this.$inputLine1 = document.querySelector(`#${this.opts.line1Id}`)
    this.$inputLine2 = document.querySelector(`#${this.opts.line2Id}`)
    this.$inputCity = document.querySelector(`#${this.opts.cityId}`)
    this.$inputCounty = document.querySelector(`#${this.opts.countyId}`)
    this.$inputPostcode = document.querySelector(`#${this.opts.postcodeId}`)

    this.addEventListeners()

  }

  getEndPoint(lookupType) {
    // The PCA API works in such a way that we have to do a 2 part lookup to 2 endpoints,
    // the first is returns a bank of addresses from which we retrieve an address ID,
    // the second returns a solid address structure based on the ID/Container passed in
    const type = LOOKUP_TYPES[lookupType] || LOOKUP_TYPES.FIND

    return `//api.addressy.com/Capture/Interactive/${type}/v1.00/json3.ws`
  }

  resetLookup() {

    DomHelpers.destroyElement(this.$resultsList)
    DomHelpers.destroyElement(this.$filter)
    DomHelpers.destroyElement(this.$dummyAddressField)

    this.$postcodeInput.value = ''

    this.$inputLine1.value = ''
    this.$inputLine2.value = ''
    this.$inputCity.value = ''
    this.$inputPostcode.value = ''
    this.$inputCounty.value = ''

    // Remove active class from search button
    this.$searchButton.classList.remove('active')

    PubSub.publish('customdropdown:update')
    PubSub.publish('addresslookup:updated', this.opts.addressId)

    this.$el.focus()
  }

  createList(items) {

    items = items || []

    const $listContainer = document.createElement('div')
    $listContainer.classList.add(SELECTORS.RESULTS_CONTAINER)

    const $list = document.createElement('ul')
    $list.classList.add(SELECTORS.RESULTS)

    if (items.length > 0) {

      items.forEach(function (item) {
        const $item = document.createElement('li')

        $item.setAttribute('data-address-id', item.Id)
        $item.innerText = [item.Text, item.Description].join(',')

        $list.appendChild($item)
      })

    } else {

      const $noItems = document.createElement('li')
      //TODO: Add additional tip(s) based on user input
      //const _value = this.$el.value
      //const _tip = '- Try entering a house number'
      $noItems.innerText = CONTENT.NO_RESULTS
      $list.appendChild($noItems)

    }

    $listContainer.appendChild($list)
    $listContainer.appendChild(this.createManualLink())

    return $listContainer

  }

  createManualLink() {

    const $manualLink = document.createElement('button')

    $manualLink.classList.add(SELECTORS.MANUAL_LINK)
    $manualLink.innerText = MANUAL_LINK_CONTENT
    if(this.opts.manualLinkAction) $manualLink.setAttribute('action', this.opts.manualLinkAction)

    return $manualLink
  }

  createDummyAddressField(value) {

    const address = value.split(',')

    const addressLine1 = address[0].replace(',', '')
    const addressLine2 = value.replace(RegExp(addressLine1 + ','), '').split(',')

    const $dummyAddressField = document.createElement('div')
    $dummyAddressField.setAttribute('class', `${SELECTORS.DUMMY_ADDRESS_FIELD} ${this.$postcodeInput.className} ps-form-field`)

    $dummyAddressField.innerHTML = `<div class="${MODULE_NAME}__dummy-address-lines">
      <span>${addressLine1}</span>
      <span>${addressLine2}</span>
      </div>
      <div>
      <button class="${SELECTORS.BUTTON_CLEAR}">${CONTENT.CLEAR_BUTTON_VALUE}</button>
      </div>`

    return $dummyAddressField

  }

  createFilter(initialSearchText) {

    const $filter = document.createElement('div')

    $filter.setAttribute('class', `${SELECTORS.FILTER_INPUT} ${this.$postcodeInput.className} ps-form-field`)
    $filter.innerHTML = `<div>
        <span class="${SELECTORS.FILTER_PLACEHOLDER}">${initialSearchText}</span>
        </div>
        <div>
          <input class="${SELECTORS.FILTER_INPUT_FIELD}" type="text" autocomplete="none" placeholder="${CONTENT.FILTER_PROMPT}">
        </div>
        <div>
          <button class="${SELECTORS.BUTTON_CLEAR}">${CONTENT.CLEAR_BUTTON_VALUE}</button>
      </div>`

    return $filter

  }

  selectAddress(e) {

    DomHelpers.destroyElement(this.$dummyAddressField)

    const addressId = e.target.dataset.addressId
    const address = e.target.innerText

    this.$dummyAddressField = this.createDummyAddressField(address)
    this.$el.appendChild(this.$dummyAddressField)

    DomHelpers.destroyElement(this.$resultsList)

    this.$postcodeInput.value = ''

    Ajax.request(this.getEndPoint(LOOKUP_TYPES.RETRIEVE), {
      method: 'POST',
      queryParams: {
        Key: API_KEY,
        Id: addressId
      },
      onLoad: this.populateFields.bind(this)
    })

  }

  populateFields(addressData) {

    const data = addressData.Items[0]
    let county = undefined

    if (!data) return console.error('No data returned for this address')

    this.$inputLine1.value = data.Line1
    this.$inputLine2.value = data.Line2
    this.$inputCity.value = data.City
    this.$inputCounty.value = data.Province
    this.$inputPostcode.value = data.PostalCode

    if (data.Province)
      county = [... this.$inputCounty.options].filter(opt => opt.text == data.Province)[0]

    this.$inputCounty.value = county ? county.value : DEFAULT_COUNTY_ID

    PubSub.publish('customdropdown:update')
    PubSub.publish('addresslookup:updated', this.opts.addressId)
  }

  /**
   * Lookup the api with a given string
   * @param {lookup} string - the search to lookup the api
  */
  lookupAddressApi(queryParameters, callBack) {

    let parameters = {
      Text: '',
      Container: '',
      Id: ''
    }

    parameters = ConfigObj.merge(parameters, queryParameters)
    parameters.Key = API_KEY

    if (this.opts.countries) parameters.Countries = this.opts.countries
    if (this.opts.limit > 0) parameters.Limit = this.opts.limit

    Ajax.request(this.getEndPoint(LOOKUP_TYPES.FIND), {
      method: 'POST',
      queryParams: parameters,
      onLoad: typeof callBack === 'function' ? callBack.bind(this) : undefined
    })

  }

  findAddressId(e) {
    const queryParameters = {
      Text: this.$postcodeInput.value
    }

    this.lookupAddressApi(queryParameters, this.getAddressResults)
  }

  getAddressResults(data) {

    if (!data) return

    const dataItem = data.Items[0]

    const queryParameters = {
      Text: dataItem.Text,
      Container: dataItem.Id
    }

    this.lookupAddressApi(queryParameters, this.setupFilteringView)
  }

  setupFilteringView(data) {

    const value = this.$postcodeInput.value.replace(/\s/g, '')

    const items = data.Items.filter(item => {
      const description = item.Description.replace(/\s/g, '')

      return description.search(RegExp(`(?:${value})`, 'ig')) > -1
    })

    this.filterData = items

    this.displayResultListings(items)

    if (items.length) this.displayFilter()
  }

  filterListings(e) {

    const key = e.data
    const value = e.target.value

    if (key === ' ') return

    const filteredResults = this.filterData.filter(item => item.Text.search(RegExp(`(?:${value})`, 'ig')) > -1)

    this.displayResultListings(filteredResults)
  }

  displayResultListings(data) {

    DomHelpers.destroyElement(this.$resultsList)

    this.$resultsList = this.createList(data)
    this.$el.appendChild(this.$resultsList)
  }

  displayFilter() {

    DomHelpers.destroyElement(this.$filter)

    this.$filter = this.createFilter(this.$postcodeInput.value)
    this.$el.appendChild(this.$filter)

    const $input = this.$filter.querySelector(`.${SELECTORS.FILTER_INPUT_FIELD}`)
    $input.focus()
  }

  /**
  * Delagates click event to constious handlers
  */
  onClick(e) {

    DomHelpers.delegateEvent(e, '[data-address-id]', this.selectAddress.bind(this))
    DomHelpers.delegateEvent(e, `.${SELECTORS.BUTTON_CLEAR}`, this.resetLookup.bind(this))
    DomHelpers.delegateEvent(e, `.${SELECTORS.BUTTON_SEARCH}`, this.findAddressId.bind(this))
    DomHelpers.delegateEvent(e, `.${SELECTORS.MANUAL_LINK}`, this.resetLookup.bind(this))

    e.preventDefault()
  }

  /**
  * Adds basic keyboard accessibility
  */
  onKeyEntry(e) {
    this.toggleSearchButtonColor(e)

    // User hit the escape key
    // Regex here for full browser support - IE emits 'Esc' while other browsers emit 'Escape' as the key and is more readable than 27 (e.which)
    if (/esc(?:ape)*/i.test(e.key)) this.resetLookup()
    if (/enter/i.test(e.key)) {
      this.findAddressId()
      e.preventDefault()
    }
  }

  toggleSearchButtonColor(e) {
    const action = e.target.value.length > 0 ? 'add' : 'remove'
    this.$searchButton.classList[action]('active')
  }

  onOuterClick(e) {

    const parent = e.target.offsetParent
    const className = e.target.className

    // If the targetted element is not a child of this component
    if ((parent && parent.className.indexOf(MODULE_NAME)) == -1 ||
      className.indexOf(MODULE_NAME) == -1) {

      DomHelpers.destroyElement(this.$resultsList)
      DomHelpers.destroyElement(this.$filter)
    }
  }

  onInput(e) {
    // do nothing if the key is spacebar
    if (/\s/.test(e.data)) return

    DomHelpers.delegateEvent(e, `.${SELECTORS.FILTER_INPUT_FIELD}`, this.filterListings.bind(this))
  }

  /**
  * Adds required event listeners to handle interactions
  */
  addEventListeners() {
    PubSub.subscribe('addresslookup:clear', this.resetLookup.bind(this))
    this.$el.addEventListener('input', this.onInput.bind(this))
    this.$el.addEventListener('click', this.onClick.bind(this))
    this.$el.addEventListener('keydown', this.onKeyEntry.bind(this))

    // Should probably have a subscribable event here rather than (potentially) bubbling lots of document.level events
    document.addEventListener('click', this.onOuterClick.bind(this))
  }
}



// WEBPACK FOOTER //
// ./js/self-serve/modules/AddressLookup.js